import { useMemo, useState } from 'react';
import { Badge, Button, Group, Stack, Text, TextInput, Title } from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { useWellsList } from '../../../queries/wells';
import { useProjectMembers } from '../../../queries/project-members';
import { useProjectStore } from '../../../project/project-store';
import { WellFormDrawer } from './WellFormDrawer';
import type { Well } from '../../../types/api';

export function WellListPage() {
  const projectId = useProjectStore((s) => s.projectId);
  const role = useProjectStore((s) => s.projectRole);
  const canWrite = role === 'owner' || role === 'moderator' || role === 'editor';

  const [q, setQ] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Well | null>(null);

  const filters = useMemo(() => ({ q: q.trim() || undefined, limit: 200 }), [q]);
  const { data: wells = [], isLoading } = useWellsList(filters);
  const { data: members } = useProjectMembers(projectId);

  if (!projectId) {
    return (
      <Stack>
        <Title order={2}>Wells</Title>
        <Text c="dimmed">Pick a project to view its wells.</Text>
      </Stack>
    );
  }

  function openCreate() {
    setEditTarget(null);
    setDrawerOpen(true);
  }

  function openEdit(w: Well) {
    setEditTarget(w);
    setDrawerOpen(true);
  }

  function lastUpdatedLabel(w: Well): string {
    const name =
      members?.find((m) => m.user_id === w.updated_by_user_id)?.username ??
      w.updated_by_user_id ??
      '—';
    return `${name} · ${new Date(w.updated_at).toLocaleDateString()}`;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end">
        <Stack gap={4}>
          <Title order={2}>Wells</Title>
          <Text c="dimmed" size="sm">
            Project-scoped wells (property #, classification, PLSS, interest matrix, and production).
          </Text>
        </Stack>
        {canWrite && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            New well
          </Button>
        )}
      </Group>

      <Group>
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search property #, name, API, operator"
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          w={320}
        />
      </Group>

      <DataTable
        withTableBorder
        highlightOnHover
        minHeight={150}
        fetching={isLoading}
        records={wells}
        idAccessor="id"
        noRecordsText="No wells yet"
        onRowClick={({ record }) => openEdit(record)}
        columns={[
          {
            accessor: 'property_number',
            title: 'Property #',
            render: (w) => (
              <Text size="sm" fw={500}>
                {w.property_number}
              </Text>
            ),
          },
          {
            accessor: 'property_name',
            title: 'Name',
            render: (w) => <Text size="sm">{w.property_name ?? '—'}</Text>,
          },
          {
            accessor: 'api',
            title: 'API',
            render: (w) => <Text size="sm">{w.api ?? '—'}</Text>,
          },
          {
            accessor: 'operator',
            title: 'Operator',
            render: (w) => <Text size="sm">{w.operator ?? '—'}</Text>,
          },
          {
            accessor: 'well_status',
            title: 'Status',
            width: 160,
            render: (w) =>
              w.well_status ? (
                <Badge variant="light" color="indigo">
                  {w.well_status}
                </Badge>
              ) : (
                <Text size="xs" c="dimmed">
                  —
                </Text>
              ),
          },
          {
            accessor: 'updated_at',
            title: 'Last updated',
            render: (w) => (
              <Text size="xs" c="dimmed">
                {lastUpdatedLabel(w)}
              </Text>
            ),
          },
        ]}
      />

      <WellFormDrawer
        opened={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditTarget(null);
        }}
        well={editTarget}
      />
    </Stack>
  );
}
